/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useTheme,
  Box,
  Tooltip,
  Typography,
  Grid,
  TextField,
} from '@mui/material';

import { LoadingButton } from '@mui/lab';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import { CustomDataGrid, DialogBox } from 'components';
import { colorTheme } from 'theme';
import DownloadIcon from '@mui/icons-material/Download';
import { useState, useEffect } from 'react';
import { downloadBankFiles, getUploadedBankFiles } from 'service';
import { useSelector, useDispatch } from 'react-redux';
import { strings } from 'i18n/Strings';
import { commonConstantTexts } from 'constants/constantText';
import { checkSortingValues, dateFormat } from 'utils/GeneralUtil';
import { useNavigate } from 'react-router-dom';
import UploadFile from '../UploadFile';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';
import { setSnackBarFailed } from 'store/snackBar';
import { getBankTransactionFile } from 'service/UploadService';

export interface UploadFileType {
  contributionFileId: number;
  fileName: string;
  key: string;
  employeeId: number;
  createdAt: Date;
}

interface BankFileFilters {
  startDate?: string | null;
  endDate?: string | null;
}

type GridRowData = Record<string, unknown>;

const UploadsGrid = (): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const [contributionDetails, setContributionDetails] = useState<
    UploadFileType[] | unknown
  >([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [pageCount, setPageCount] = useState(null);
  const pageInfo = useSelector((state) => state?.pagination?.uploads);
  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState<BankFileFilters>({
    startDate: null,
    endDate: null,
  });
  const [onUploadError, setOnUploadError] = useState({
    isFullScreen: false,
    screenWidth: 'sm',
  });

  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setSearchValue(event.target.value);
  };

  const handleCreateDialogClose = (): void => {
    setIsModalOpen(false);
    setOnUploadError({
      isFullScreen: false,
      screenWidth: 'sm',
    });
  };
  const getAllBankFilesRequests = async (): Promise<void> => {
    try {
      const data = await getUploadedBankFiles(
        Number(pageInfo?.page) + 1,
        pageInfo?.pageSize,
        pageInfo?.sortBy,
        pageInfo?.sortOrder,
        searchValue,
        filters,
      );
      setContributionDetails(data?.rows);
      setPageCount(data?.count);
    } catch (error) {
      console.log(error);
    }
  };

  const handleFilterChange = (filterType: string, value: string): void => {
    setFilters({
      ...filters,
      [filterType]: value,
    });
  };

  const handleDownloadBankFiles = async (
    key: string,
    fileName: string,
  ): Promise<void> => {
    try {
      const data = await downloadBankFiles(key, fileName);
      const link = document.createElement('a');
      link.href = data;
      document.body.appendChild(link);
      link.click();

      // Clean up the link
      document.body.removeChild(link);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    void getAllBankFilesRequests();
  }, [
    pageInfo?.page,
    pageInfo?.pageSize,
    searchValue,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
  ]);

  const handleApplyFilter = async (): Promise<void> => {
    dispatch(setCurrentPage({ key: 'uploads', value: 0 }));

    void getAllBankFilesRequests();
  };

  const handleExport = async (): Promise<void> => {
    setExportLoader(true);

    if (filters.startDate && filters.endDate) {
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      const dateDifference =
        (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24);

      if (dateDifference > 60) {
        setExportLoader(false);
        dispatch(
          setSnackBarFailed({
            snackBarMessage: strings.displayText.dateExceeded,
          }),
        );
        return;
      }
    }
    if (!filters.startDate || !filters.endDate) {
      setExportLoader(false);
      dispatch(
        setSnackBarFailed({
          snackBarMessage: strings.displayText.selectDateRange,
        }),
      );
      return;
    }
    try {
      const url = await getBankTransactionFile(
        filters.startDate,
        filters.endDate,
      );
      const link = document.createElement('a');
      link.href = url as string;
      link.download = 'Transaction_Report.xlsx';
      link.click();
    } catch (error) {
      console.error('Error exporting Bank File Data:', error);
    } finally {
      setExportLoader(false);
    }
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'contributionFileId',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.bankFileId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.contributionFileId}
        </Typography>
      ),
    },
    {
      field: 'fileName',
      width: 350,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fileName}
        </Typography>
      ),
      renderCell: ({ row }) => {
        const name: string = row.fileName;
        return (
          <Tooltip title={name} placement="top">
            <Typography
              variant="h5"
              color={colors.gray[100]}
              fontSize={14}
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '95%',
                display: 'block',
              }}
            >
              {name}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'createdAt',
      width: 190,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.bankFileUploadDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row.createdAt)}
        </Typography>
      ),
    },
    {
      field: 'employeeId',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.userID}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.employeeId}
        </Typography>
      ),
    },
    {
      field: 'downloadFile',
      sortable: false,
      align: 'center',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.downloadFile}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          <DownloadIcon
            onClick={() => {
              void handleDownloadBankFiles(row.key, row.fileName);
            }}
          />
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={6} />
        <Grid sx={{ minWidth: 90, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.startDate}
            type="date"
            value={filters.startDate}
            onChange={(e) => {
              handleFilterChange('startDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid sx={{ minWidth: 90, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.endDate}
            type="date"
            value={filters.endDate}
            onChange={(e) => {
              handleFilterChange('endDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        <Grid item>
          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            loadingPosition="end"
            onClick={() => {
              void handleApplyFilter();
            }}
          >
            {strings.displayText.filter}
          </LoadingButton>
        </Grid>

        <Grid item>
          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            loadingPosition="end"
            onClick={handleExport}
            loading={exportLoader}
          >
            Export
          </LoadingButton>
        </Grid>
      </Grid>
      <CustomDataGrid
        title={strings.displayText.banFilesTitle}
        columns={columns}
        rows={contributionDetails as GridRowData[]}
        addButtonTitle={strings.displayText.uploadButton}
        onAddButtonClick={() => {
          setIsModalOpen(true);
        }}
        onRowClick={(row) => {
          navigate(`/upload-details/${row.id}`);
        }}
        getRowId={(row: GridRowData) =>
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          `${String(row.contributionFileId)}`
        }
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0
            ? commonConstantTexts.evenText
            : commonConstantTexts.oddText
        }
        currentPage="uploads"
        pageCount={pageCount}
        searchEnabled={true}
        handleSearch={handleSearch}
        searchPlaceholder={strings.displayText.searchLabel7}
        sortModel={checkSortingValues(pageInfo)}
        sortingMode={strings.displayText.sortingServer}
        onSortModelChange={(mode) => {
          dispatch(
            setSortBy({
              key: 'uploads',
              value: mode[0].field,
            }),
          );
          dispatch(
            setSortOrder({
              key: 'uploads',
              value: mode[0].sort,
            }),
          );
          dispatch(setCurrentPage({ key: 'uploads', value: 0 }));
        }}
      />
      <DialogBox
        open={isModalOpen}
        onClose={handleCreateDialogClose}
        title={strings.displayText.uploadBankFile}
        className={''}
        fullWidth={onUploadError.isFullScreen}
        screenWidth={onUploadError.screenWidth}
      >
        <UploadFile
          getBankFile={getAllBankFilesRequests}
          onClose={() => {
            setIsModalOpen(!isModalOpen);
            setOnUploadError({
              isFullScreen: false,
              screenWidth: 'sm',
            });
          }}
          onError={() => {
            setOnUploadError({
              isFullScreen: true,
              screenWidth: 'lg',
            });
          }}
        />
      </DialogBox>
    </Box>
  );
};

export default UploadsGrid;
